import { FormControlLabel, Radio, RadioGroup, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { IBottomSheetContext } from 'src/components/shared/bottom-sheet/types';
import { sortActions } from 'src/store/SortSlice';
import { useSelector } from 'src/store/Store';
import { StyledRoundButton } from 'src/styles/button';
import { SORT_OPTIONS, SORT_TYPE } from 'src/types/sort';

type Props = {
  bottomSheet: IBottomSheetContext;
};

const StyledWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

type SortMapping = {
  name: string;
  label: string;
  field: SORT_OPTIONS;
  type: SORT_TYPE;
};

export const SORT_MAPPINGS: SortMapping[] = [
  {
    name: 'APPLICATION_DATE_DESC',
    label: 'Application date (newest first)',
    field: SORT_OPTIONS.APPLICATION_DATE,
    type: SORT_TYPE.DESC,
  },
  {
    name: 'APPLICATION_DATE_ASC',
    label: 'Application date (oldest first)',
    field: SORT_OPTIONS.APPLICATION_DATE,
    type: SORT_TYPE.ASC,
  },
  {
    name: 'OVERALL_SCORE_DESC',
    label: 'Score (highest first)',
    field: SORT_OPTIONS.OVERALL_SCORE,
    type: SORT_TYPE.DESC,
  },
  {
    name: 'OVERALL_SCORE_ASC',
    label: 'Score (lowest first)',
    field: SORT_OPTIONS.OVERALL_SCORE,
    type: SORT_TYPE.ASC,
  },
  {
    name: 'CREATION_DATE_DESC',
    label: 'Creation date (newest first)',
    field: SORT_OPTIONS.CREATION_DATE,
    type: SORT_TYPE.DESC,
  },
  {
    name: 'CREATION_DATE_ASC',
    label: 'Creation date (oldest first)',
    field: SORT_OPTIONS.CREATION_DATE,
    type: SORT_TYPE.ASC,
  },
];

const Sort = ({ bottomSheet }: Props) => {
  const { t } = useTranslation();
  const { field, type } = useSelector((state) => state.sort);
  const dispatch = useDispatch();

  const defaultSort = SORT_MAPPINGS.find((m) => m.name === 'APPLICATION_DATE_DESC');
  const selectedOption = SORT_MAPPINGS.find((m) => m.field === field && m.type === type);
  const [tempSort, setTempSort] = useState<SortMapping | null>(
    selectedOption ?? defaultSort ?? null,
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const foundOption = SORT_MAPPINGS.find(
      (m) => m.name === (event.target as HTMLInputElement).value,
    );
    setTempSort(foundOption ?? null);
  };

  const handleApplyFilters = () => {
    if (!tempSort) return;

    dispatch(sortActions.setSort({ field: tempSort.field, type: tempSort.type } ?? null));
    bottomSheet?.close?.();
  };

  return (
    <StyledWrapper>
      <Typography variant="h3">{t('Sorting options')}</Typography>

      <RadioGroup
        aria-labelledby="demo-controlled-radio-buttons-group"
        name="controlled-radio-buttons-group"
        value={tempSort?.name ?? null}
        onChange={handleChange}
      >
        {SORT_MAPPINGS.map((m) => (
          <FormControlLabel key={m.name} value={m.name} control={<Radio />} label={t(m.label)} />
        ))}
      </RadioGroup>

      <StyledRoundButton variant="contained" onClick={handleApplyFilters}>
        {t('Apply')}
      </StyledRoundButton>
      <StyledRoundButton variant="outlined" onClick={bottomSheet.close}>
        {t('Discard')}
      </StyledRoundButton>
    </StyledWrapper>
  );
};

export default Sort;
